import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { House, ChartBar, GameController, Scales } from "@phosphor-icons/react";
import { loadValueOfDay } from "../lib/valueOfDay";
import { isValueEnabled } from "../lib/features";
import { StatusBadge } from "./StatusBadge";

const TABS = [
  { to: "/", labelKey: "nav.home", icon: House, key: "accueil" },
  { to: "/value", labelKey: "nav.value", icon: Scales, key: "value" },
  { to: "/stats", labelKey: "nav.stats", icon: ChartBar, key: "stats" },
  { to: "/play", labelKey: "nav.play", icon: GameController, key: "play" },
];

export const BottomNav = () => {
  const { t } = useTranslation();
  const loc = useLocation();
  const [newCount, setNewCount] = React.useState(0);
  // Un seul chargement « Value du jour » par changement d'écran, partagé entre le badge et l'acquittement.
  React.useEffect(() => {
    if (!isValueEnabled()) return;  // fonctionnalité « Value » retirée
    const onValue = loc.pathname.startsWith("/value");
    loadValueOfDay().then((d) => {
      const n = d?.new_count || 0;
      if (onValue) {
        localStorage.setItem("value_badge_ack", String(n));
        setNewCount(0);
        return;
      }
      const ack = parseInt(localStorage.getItem("value_badge_ack") || "0", 10);
      setNewCount(n > ack ? n : 0);
    }).catch(() => { if (onValue) setNewCount(0); });
  }, [loc.pathname]);
  // Onglets réellement affichés (+ 1 colonne pour le profil) : la barre se répartit toujours sur toute la
  // largeur, sans colonne vide depuis le retrait de l'onglet « Value ».
  const tabs = TABS.filter((tab) => tab.key !== "value" || isValueEnabled());
  return (
    <nav className="fixed bottom-0 inset-x-0 z-50 glass border-t border-white/10" data-testid="bottom-nav">
      <div className="max-w-2xl mx-auto grid" style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, minmax(0, 1fr))` }}>
        {tabs.map((tab) => {
          const active = tab.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(tab.to);
          const Icon = tab.icon;
          const showBadge = tab.key === "value" && newCount > 0;
          return (
            <NavLink
              key={tab.key}
              to={tab.to}
              data-testid={`bottom-tab-${tab.key}`}
              className="flex flex-col items-center gap-1 py-3 relative"
            >
              {active && (
                <span className="absolute top-0 w-10 h-0.5 rounded-full bg-gold glow-gold" />
              )}
              <div className="relative">
                <Icon size={24} weight={active ? "fill" : "regular"} color={active ? "#D4AF37" : "rgba(255,255,255,0.5)"} />
                {showBadge && (
                  <span
                    data-testid="value-unread-badge"
                    className="absolute -top-1 -right-1.5 min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-extrabold leading-none animate-pulse"
                    style={{ boxShadow: "0 0 8px rgba(239,68,68,0.8)" }}
                  >
                    {newCount}
                  </span>
                )}
              </div>
              <span className={`text-[10px] font-semibold ${active ? "text-gold" : "text-white/50"}`}>{t(tab.labelKey)}</span>
            </NavLink>
          );
        })}
        <div data-testid="bottom-tab-profil" className="flex items-stretch justify-center">
          <StatusBadge variant="nav" />
        </div>
      </div>
    </nav>
  );
};

export const AppLayout = ({ children }) => (
  <div className="min-h-screen grain relative">
    <div className="max-w-2xl mx-auto pb-24 relative z-10">{children}</div>
    <BottomNav />
  </div>
);
